import React, { useContext, useMemo } from "react"

import { ColorSwatch, TagSelect, type TagSelectOption } from "@coval/ui"

import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"
import { InlineFilterPanelContext } from "@/contexts/InlineFilterPanelContext"

import { useOrganizationTags } from "../routes/useOrganizationTags"
import { buildTagFilterItems } from "../utils/tagCatalog"
import { normalizeTags } from "../utils/tagNormalization"

interface TagPickerProps {
  selectedTags: string[]
  onSelectedTagsChange: (tags: string[]) => void
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  /** Whether closing the picker should return focus to its trigger. */
  restoreFocusOnClose?: boolean
  /** The control's own affordance. Must be a single focusable element. */
  trigger: React.ReactElement
  /** Set false to hold the tag catalog request until the menu is wanted. */
  enabled?: boolean
  /** Renders the loading row while the catalog request is still deferred. */
  isDeferred?: boolean
  side?: "bottom" | "left" | "right" | "top"
  align?: "center" | "end" | "start"
  emptyMessage?: string
  noItemsMessage?: string
}

/**
 * The organization's tags as a selectable menu, behind a caller-supplied
 * trigger. Wraps the design system's `TagSelect` so that choosing tags to
 * filter or target by looks the same as choosing tags to assign, and keeps the
 * catalog fetch in one place rather than in each page that offers the control.
 *
 * Selection-only: creating, renaming, and recoloring belong to `TagEditor`,
 * which owns the mutations.
 */
export const TagPicker: React.FC<TagPickerProps> = ({
  selectedTags,
  onSelectedTagsChange,
  isOpen,
  onOpenChange,
  restoreFocusOnClose = true,
  trigger,
  enabled = true,
  isDeferred = false,
  side = "bottom",
  align = "start",
  emptyMessage = "No tags found",
  noItemsMessage = "No tags yet",
}) => {
  const inline = useContext(InlineFilterPanelContext)
  const { suggestions, isLoading } = useOrganizationTags({ enabled })

  const options = useMemo<TagSelectOption[]>(
    () =>
      buildTagFilterItems({ selectedTags, suggestions }).map((item) => ({
        value: item.tag,
        label: item.tag,
        color: item.color ?? undefined,
      })),
    [selectedTags, suggestions],
  )

  if (inline) {
    return (
      <SearchableCheckboxList
        items={options.map((option) => ({
          id: option.value,
          label: option.label,
        }))}
        selectedIds={normalizeTags(selectedTags)}
        onSelectionChange={(tags) => onSelectedTagsChange(normalizeTags(tags))}
        isLoading={isLoading}
        isDeferred={isDeferred}
        isOpen={isOpen}
        placeholder="Search tags..."
        emptyMessage={emptyMessage}
        noItemsMessage={noItemsMessage}
        capitalizeLabels={false}
        width="w-full"
        renderItem={(item) => {
          const color = options.find(
            (option) => option.value === item.id,
          )?.color
          return (
            <span className="flex items-center gap-2">
              {color && <ColorSwatch color={color} />}
              {item.label}
            </span>
          )
        }}
      />
    )
  }

  return (
    <TagSelect
      options={options}
      value={normalizeTags(selectedTags)}
      onValueChange={(tags) => onSelectedTagsChange(normalizeTags(tags))}
      open={isOpen}
      onOpenChange={onOpenChange}
      restoreFocusOnClose={restoreFocusOnClose}
      trigger={trigger}
      side={side}
      align={align}
      avoidCollisions
      isLoading={isDeferred || isLoading}
      emptyLabel={noItemsMessage}
      noResultsLabel={emptyMessage}
      searchPlaceholder="Search tags..."
    />
  )
}
